import { FileUploader, ProgressBar } from '@bigcommerce/big-design';
import { FileUploaderBox } from './styled';
import { resizeImage } from '../../utils';

export type FileWithDimensions = {
  fileData: File;
  width?: number;
  height?: number;
}

export type FileWithoutDimensions = Array<File>;

// Get image dimensions
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = () => reject(new Error('Image failed to load'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

interface WFileUploaderProps {
  onFilesChange: (files: FileWithDimensions[] | File[]) => void;
  imageDimensionRequired?: boolean;
  isLoading?: boolean;
  showProgressBar?: boolean
}

const WFileUploader = ({ onFilesChange, imageDimensionRequired = false, showProgressBar = false, isLoading = false }: WFileUploaderProps) => {
  const validFileTypes = ["image/jpeg", "image/png", "image/jpg"];
  
  const onChange = async (files: File[]) => {
    // Filter valid file types
    const validFiles = files.filter(file => validFileTypes.includes(file.type)) as File[];
    
    if (validFiles.length === 0) {
      onFilesChange([]);
      return;
    }

    try {
      // Resize all images first
      const resizedFiles = await Promise.all(
        validFiles.map((file) => resizeImage(file))
      );

      if (imageDimensionRequired) {
        // Process files with dimension retrieval
        const filesWithDimensions = await Promise.all(
          resizedFiles.map(async (file) => {
            const dimensions = await getImageDimensions(file);
            return {
              fileData: file,
              ...dimensions
            };
          })
        );
        onFilesChange(filesWithDimensions);
      } else {
        onFilesChange(resizedFiles);
      }
    } catch (error) {
      console.error('Error processing images:', error);
      onFilesChange([]);
    }
  };

  return (
    <FileUploaderBox marginVertical="xSmall">
      {showProgressBar && isLoading ? <ProgressBar />: null }
      <FileUploader
        dropzoneConfig={{
          label: 'Drag and drop image here',
        }}
        files={[]}
        label=""
        multiple
        onFilesChange={onChange}
        required
      /> 
    </FileUploaderBox>
  );
};

export default WFileUploader;